/**
 * Which day does the wastage screen open on?
 *
 * WHY THIS EXISTS
 *
 * The form seeded its date from `recoveryDate || today`, and App.js fed
 * recoveryDate from `yesterdayDate` — the LOCK's date. That value answers "which
 * day should the lock punish?", and it is deliberately null for the whole
 * 00:00–02:00 grace window (and is only passed at all while the lock is active).
 *
 * "Which day is this wastage FOR?" has the opposite timing. It is most likely to
 * be yesterday exactly while the grace is still running, because that is when a
 * restaurant that closed after midnight sits down to file. So for that whole
 * window the form opened on the new calendar day and last night's wastage / NC
 * was stamped on tomorrow.
 *
 * Measured on live data: 282 of 318 wastage records filed between 00:00 and
 * 05:00 IST landed on the wrong day (89%).
 *
 * The TSR screen had this exact defect and was fixed with the server's
 * clock-free filing date (utils/tsrFilingDate.js -> today-status.suggestedTsrDate).
 * Wastage precedes the TSR in the daily flow, so the day the TSR is owed for IS
 * the day the wastage is for. This reuses that answer rather than inventing a
 * second one that could disagree with it.
 *
 * DELIBERATELY TAKES NO CLOCK. `today` is passed in as a calendar day; nothing
 * here looks at the hour. The time-of-day dependence WAS the bug — if a future
 * change wants to pass an hour in here, that is the defect coming back.
 *
 * Pure functions, so the rule is testable without mounting a 5,000-line form.
 */
import { isWastageDateDisabled, parseYmd } from './wastageDateBounds';

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Is `ymd` a usable, selectable wastage day given what is known right now? */
function isUsable(ymd, { today, reconciledThrough }) {
  if (typeof ymd !== 'string' || !YMD.test(ymd)) return false;
  const day = parseYmd(ymd);
  const now = parseYmd(today);
  if (!day || !now) return false;
  // Same rule the calendar applies cell by cell: no future days, nothing on or
  // before the reconciled watermark. One definition, so the seed can never be a
  // day the picker itself would refuse to offer.
  return !isWastageDateDisabled(day, { reconciledThrough: reconciledThrough || null, now });
}

/**
 * The day the form should open on.
 *
 * @param {object} p
 * @param {string|null} p.recoveryDate       set by a recovery navigation; fixes the date
 * @param {string|null} p.suggestedDate      today-status.suggestedTsrDate
 * @param {string}      p.today              IST calendar day, 'YYYY-MM-DD'
 * @param {string|null} [p.reconciledThrough]
 * @returns {{date: string, source: 'recovery'|'suggested'|'today'}}
 *   `source: 'suggested'` means the form opened on a BACKLOG day — the caller
 *   should say so on screen, because a silent default is how a user ends up
 *   filing today's wastage against yesterday without noticing.
 */
function resolveWastageSeedDate({ recoveryDate, suggestedDate, today, reconciledThrough = null }) {
  if (recoveryDate) return { date: recoveryDate, source: 'recovery' };

  if (
    suggestedDate &&
    suggestedDate !== today &&
    isUsable(suggestedDate, { today, reconciledThrough })
  ) {
    return { date: suggestedDate, source: 'suggested' };
  }

  return { date: today, source: 'today' };
}

/**
 * The suggestion is fetched by App.js and usually arrives AFTER this form has
 * mounted on today. Should the form move to it now?
 *
 * One shot, never over a deliberate choice, never in a recovery flow, never
 * into a closed period — the same guards the TSR screen applies to the same
 * value, for the same reasons.
 */
function shouldAdoptSuggestedDate({
  suggestedDate,
  today,
  currentDate,
  recoveryDate,
  userPicked,
  alreadyApplied,
  reconciledThrough = null,
}) {
  if (!suggestedDate || alreadyApplied || userPicked || recoveryDate) return false;
  if (suggestedDate === currentDate) return false;
  return isUsable(suggestedDate, { today, reconciledThrough });
}

export { resolveWastageSeedDate, shouldAdoptSuggestedDate };
